import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { CheckCircle2 } from "lucide-react";

const LOGO_STACKED = "https://customer-assets.emergentagent.com/job_tpl-roofing/artifacts/x4oygmg8_LOGO.webp";

const STRENGTHS = [
  "Expertise en toiture commerciale et industrielle",
  "Plus de 10 employés qualifiés et certifiés",
  "Gestion de contrats jusqu'à 1 million de dollars",
  "Service dans 5 secteurs d'activité",
  "Imperméabilisation de haute performance",
  "Intervention rapide et professionnelle",
];

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="a-propos"
      data-testid="about-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Text content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="tpl-overline mb-4">À propos</p>
            <h2 className="text-4xl md:text-5xl tracking-tight font-bold text-[#0F172A] mb-6 leading-tight">
              Votre partenaire
              <br />
              <span className="text-[#0077cc]">de confiance</span> en toiture
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#475569] mb-8">
              Toitures & Imperméabilisations TPL inc. est une entreprise québécoise
              spécialisée dans les travaux de toiture et d'imperméabilisation. Située à
              Mirabel, notre équipe de professionnels qualifiés dessert les secteurs
              commercial, industriel, institutionnel, de la santé et résidentiel.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#475569] mb-10">
              Avec une équipe de 10 à 50 employés expérimentés et la capacité de gérer
              des contrats allant jusqu'à 1 million de dollars, nous offrons un service
              complet et fiable pour tous vos besoins en toiture.
            </p>

            {/* Strengths grid */}
            <div className="grid sm:grid-cols-2 gap-3">
              {STRENGTHS.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                  className="flex items-start gap-2"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#0077cc] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[#0F172A] font-medium">{s}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Dark brand block */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-[#0A0F1C] p-12 md:p-16 flex flex-col items-center justify-center min-h-[400px]">
              <img
                src={LOGO_STACKED}
                alt="TPL inc"
                className="w-48 md:w-64 mb-8 opacity-90"
              />
              <div className="text-center">
                <p className="text-white/50 text-sm uppercase tracking-[0.2em] mb-2">Établie à</p>
                <p className="text-white font-bold text-lg">Mirabel, Québec</p>
              </div>
              {/* Decorative red accent */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[#e62e37]" />
              <div className="absolute bottom-0 right-0 w-1 h-24 bg-[#0077cc]" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
